import { Controller } from '@hotwired/stimulus';
import type {
  Calendar,
  DateClickArg,
  EventClickArg,
  EventSourceFuncArg,
} from '../../services/events_calendar/fullcalendar';
import { eventsUrl, newEventUrl, previewUrl } from '../../services/events_calendar/urls';

// The Calendar tab on Manage Events. FullCalendar is imported lazily so it is
// only downloaded when this tab is opened, not on every page.
export default class extends Controller {
  static targets = ['calendar', 'mini'];
  static values = {
    eventsUrl: String,
    previewUrl: String,
    newEventUrl: String,
    createMode: String,
    filters: String,
  };

  declare calendarTarget: HTMLElement;
  declare miniTarget: HTMLElement;
  declare eventsUrlValue: string;
  declare previewUrlValue: string;
  declare newEventUrlValue: string;
  declare createModeValue: string;
  declare filtersValue: string;

  private calendar: Calendar | null = null;
  private mini: Calendar | null = null;
  private disconnected = false;

  // The app loads pages over AJAX, so following View Event never unloads the
  // popup. Hide it on the click and let the navigation go ahead.
  private closePreviewOnViewEvent = (event: MouseEvent) => {
    const link = (event.target as Element | null)?.closest('.events-calendar-preview-view-event');
    if (!link) return;
    const modal = link.closest('.modal');
    if (modal) $(modal).modal('hide');
  };

  async connect() {
    document.addEventListener('click', this.closePreviewOnViewEvent);
    this.disconnected = false;
    const { buildCalendar, buildMiniCalendar } = await import(
      '../../services/events_calendar/fullcalendar'
    );
    if (this.disconnected) return;

    this.calendarTarget.innerHTML = '';
    this.calendar = buildCalendar(this.calendarTarget, {
      fetchEvents: this.fetchEvents.bind(this),
      onEventClick: this.openPreview.bind(this),
      onDateClick: this.createAt.bind(this),
      onDatesSet: (start: Date) => this.mini?.gotoDate(start),
    });
    this.mini = buildMiniCalendar(this.miniTarget, (date: Date) => this.calendar?.gotoDate(date));
    this.calendar.render();
    this.mini.render();
  }

  disconnect() {
    document.removeEventListener('click', this.closePreviewOnViewEvent);
    this.disconnected = true;
    this.calendar?.destroy();
    this.mini?.destroy();
    this.calendar = null;
    this.mini = null;
  }

  private async fetchEvents(range: EventSourceFuncArg) {
    const response = await fetch(
      eventsUrl(this.eventsUrlValue, range.start, range.end, this.filtersValue),
      {
        credentials: 'same-origin',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      },
    );
    if (!response.ok) throw new Error(`Calendar events request failed (${response.status})`);
    return response.json();
  }

  private openPreview(info: EventClickArg) {
    info.jsEvent.preventDefault();
    const trigger = document.createElement('span');
    trigger.dataset.url = previewUrl(this.previewUrlValue, info.event.id);
    defaultPopupLauncher(trigger);
  }

  private createAt(info: DateClickArg) {
    if (this.createModeValue === 'form') {
      window.location.href = newEventUrl(this.newEventUrlValue, info.date, info.allDay);
    } else if (this.createModeValue === 'modal') {
      $('#event-modal').modal('show');
    }
  }
}
